<script lang="ts">
	import { iconValues, type Icon } from '#lib/types/icons.js';
	import type { Component } from 'svelte';

	interface Props {
		src: Icon;
		className?: string;
		style?: string;
	}

	let { src, className = '', style = '' }: Props = $props();

	const localSvgModules = import.meta.glob<{ default: Component<any> }>('./*.svg.svelte', {
		eager: true
	});

	const localSvgMap: Record<string, Component<any> | undefined> = Object.fromEntries(
		Object.entries(localSvgModules).map(([path, module]) => [
			path.replace(/^\.\//, '').replace(/\.svg\.svelte$/, ''),
			module.default
		])
	);

	async function loadComponent(icon: string): Promise<Component<any> | null> {
		if (icon === '') return null;

		const localComponent = localSvgMap[icon];
		if (localComponent) {
			return localComponent;
		}

		if (iconValues.includes(icon)) {
			console.error(`Icon "${icon}" not found in local SVGs`);
			return null;
		}

		try {
			const module = (await import('@lucide/svelte')) as unknown as Record<
				string,
				Component<any> | undefined
			>;
			if (module[icon]) {
				return module[icon];
			}
		} catch {
			throw new Error(`Icon "${icon}" not found in @lucide/svelte`);
		}

		throw new Error(`Icon "${icon}" not found`);
	}
	let loadedComponent = $state<Component<any> | null>(null);
	let notFound = $state(false);

	$effect(() => {
		let active = true;
		loadedComponent = null;
		notFound = false;

		loadComponent(src).then((comp) => {
			if (active) {
				loadedComponent = comp;
				if (!comp && src !== '') {
					notFound = true;
				}
			}
		});

		return () => {
			active = false;
		};
	});
</script>

{#if loadedComponent}
	{@const Component = loadedComponent}
	<Component
		className={className + ' flex-none'}
		class={className + ' flex-none'}
		{style}
		strokeWidth={1.25}
	/>
{:else if notFound}
	<p class="bg-error text-error-on">Icon {src} not found</p>
{/if}
